import { forwardRef, ReactNode } from 'react';
import { chakra, ChakraProps, createStylesContext, IconProps, useMultiStyleConfig } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import Text from '../Text/Text';
import Icon, { TypeIconName } from '../Icon/Icon';
import Link from '../Link/Link';

type SidebarItemProps = ChakraProps & {
  children: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  href?: string;
  isExternal?: boolean;
} & (
    | {
        href: string;
        isExternal?: boolean;
      }
    | {
        onClick?: () => void;
        isExternal?: never;
      }
  );

const [SidebarItemStyleProvider, useSidebarItemStyle] = createStylesContext('SidebarItem');

const sidebarVariant = ({ disabled, selected }: { selected?: boolean; disabled?: boolean }) => {
  if (disabled) {
    return 'disabled';
  }
  if (selected) {
    return 'selected';
  }
  return undefined;
};

const SidebarItem = forwardRef(
  (
    { children, disabled, href, isExternal, onClick, selected, ...rest }: SidebarItemProps,
    ref: React.Ref<HTMLButtonElement & HTMLAnchorElement>,
  ) => {
    const style = useMultiStyleConfig('SidebarItem', { variant: sidebarVariant({ disabled, selected }) });
    const shared = {
      ...(rest as ChakraProps),
      children: <SidebarItemStyleProvider value={style}>{children}</SidebarItemStyleProvider>,
      height: '48',
      href,
      onClick,
      position: 'relative',
      sx: style.link,
    } as ChakraProps;
    if (!href || disabled) {
      return <chakra.button ref={ref} disabled={disabled} {...shared} />;
    }
    return <Link ref={ref} {...shared} isExternal={isExternal} />;
  },
);

const SidebarItemIcon = ({ name, ...iconProps }: { name: TypeIconName } & Omit<IconProps, 'sx'>) => {
  const { icon } = useSidebarItemStyle();
  return <Icon name={name} size="24" sx={icon} {...iconProps} />;
};

const SidebarItemLabel = ({ children }: { children: ReactNode }) => (
  <Text as="span" flexGrow="1" textAlign="left">
    {children}
  </Text>
);

const SidebarItemDateExtra = ({ date }: { date: Date | DateTime }) => {
  const dateTime = DateTime.isDateTime(date) ? date : DateTime.fromJSDate(date);
  return (
    <Text
      as="time"
      dateTime={dateTime.toFormat('yyyy-LL-dd')}
      fontWeight="normal"
      minWidth="5rem"
      paddingEnd="24"
      textAlign="left"
      textColor="text.secondary"
      whiteSpace="nowrap"
    >
      {dateTime.toFormat('LLL d')}
    </Text>
  );
};

export default SidebarItem;
export { SidebarItemIcon, SidebarItemLabel, SidebarItemDateExtra };
